/**
 * Landing page — the bedd.dev BIMAssess marketing homepage, ported into the
 * bimassess SPA.
 *
 * Implementation notes
 * --------------------
 * Source: Wayback Machine snapshot of https://bimassess.bedd.dev/ (2026-05-21).
 * The original page is self-contained — Google-fonts-only assets, ~1650 lines
 * of hand-authored CSS, a single inline IIFE for scroll reveals / counters /
 * magnetic-button micro-interactions, and zero external images (all visuals
 * are CSS gradients + inline SVG).
 *
 * Approach: dangerouslySetInnerHTML the cleaned markup so we ship the exact
 * visual contract the contractor authored, without retyping 1650 lines into
 * JSX. CTAs are rewired at build time (string substitution in markup.ts) to
 * point at SPA auth routes; subpage nav links open the live bedd.dev origin
 * in a new tab.
 *
 * Wayback wrappers checked for and not present — the contractor's HTML was
 * captured cleanly. Inline analytics: none. Inline script: kept, re-run from
 * useEffect after the markup mounts.
 *
 * Routing concession: anchor clicks inside the dangerously-set HTML go through
 * the browser, not react-router. The CTAs we rewired use real paths
 * (`/sign-in`, `/sign-up`), so the BrowserRouter picks them up after the full
 * navigation — accepted as a one-tick navigation cost on the marketing page.
 * If this becomes a UX issue, intercept clicks via the wrapper and call
 * navigate() instead.
 *
 * Auth-awareness (updated 2026-05-30)
 * -------------------------------------
 * Marketing pages ALWAYS render — no auto-redirect for any auth state.
 *
 * On mount, the page silently probes `/api/me` (raw fetch — bypasses the
 * `apiFetch` 401→/sign-in interceptor so anonymous visitors are never
 * bounced). The probe result drives the nav CTA group only:
 *
 *   - 401 / network error / 200-but-no-user → anonymous. Nav shows "Sign in" +
 *     "Get started" (the default marketing CTAs, already in the markup).
 *     NOTE: `/api/me` returns 200 `{ ok: true, data: null }` for sessions with
 *     no active user — so we check `data != null`, not just `res.ok` / status.
 *   - 200 with a real user in `data` (authed) → resolve the active-context home
 *     via `/api/me/workspaces`
 *     (same logic as DashboardGuard), then swap the nav CTA to a single
 *     "Go to dashboard →" link pointing at the resolved route.
 *
 * During the probe (`probing` state), the page renders the full marketing
 * content normally. The CTA group stays in its default ("Sign in / Get started")
 * until the probe resolves — a brief settle is acceptable; the page is always
 * visible and never blank.
 *
 * No navigate() / redirect anywhere in this file. The page is the destination.
 *
 * Redirect-loop prevention: `/` is in `isPublicAuthPage()` in api-client.ts,
 * so `apiFetch` 401s on this route are never redirected. The probe itself uses
 * raw `fetch()`, not `apiFetch`, so it has no interceptor at all.
 */

import React, { useEffect, useRef, useState } from 'react';
import { landingMarkup } from './landing-assets/markup';
import './marketing-shared/marketing-shared.css';
import './landing-assets/landing.css';

// ─── Auth probe types (mirrors DashboardGuard / api-client shapes) ────────────

interface WorkspacesData {
  ok: boolean;
  data?: {
    active_context: { kind: 'personal' | 'company' | 'admin'; company_id?: string };
    companies: Array<{ company_id: string }>;
  };
}

/**
 * Resolve the correct dashboard root from an active_context response, using
 * the same rules as DashboardGuard + RoleBasedRedirect.
 */
function resolveContextHome(data: WorkspacesData['data']): string {
  if (!data || !data.active_context) return '/dashboard';
  const ctx = data.active_context;
  if (ctx.kind === 'admin') return '/admin';
  if (ctx.kind === 'company' && ctx.company_id) {
    return `/company/${ctx.company_id}/dashboard`;
  }
  // personal, or fallback
  if (ctx.kind === 'personal') return '/dashboard';
  // No active context — pick first company membership if any
  if (data.companies.length > 0) {
    return `/company/${data.companies[0]!.company_id}/dashboard`;
  }
  return '/dashboard';
}

// ─── Component ────────────────────────────────────────────────────────────────

type AuthProbeState = 'probing' | 'anonymous' | 'authed';

export function LandingPage(): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [authState, setAuthState] = useState<AuthProbeState>('probing');
  // Resolved dashboard root — populated when probe returns authed.
  const dashboardHref = useRef<string>('/dashboard');

  // ─── AUTH PROBE ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      // Step 1: is the user logged in at all?
      let meRes: Response;
      try {
        meRes = await fetch('/api/me', { credentials: 'same-origin' });
      } catch {
        // Network error — treat as anonymous (marketing shows normally).
        if (!cancelled) setAuthState('anonymous');
        return;
      }

      if (cancelled) return;

      // Anonymous: either a non-OK status (e.g. 401) OR ok-but-no-user
      // (/api/me returns 200 { ok: true, data: null } when there is no session).
      // Treat "authed" only when the response carries an actual user object.
      let meData: { ok: boolean; data: unknown } | null = null;
      try {
        meData = (await meRes.json()) as { ok: boolean; data: unknown };
      } catch {
        // Unparseable body — treat as anonymous.
        setAuthState('anonymous');
        return;
      }

      if (!meRes.ok || !meData?.ok || !meData?.data) {
        // Anonymous visitor — show marketing normally.
        setAuthState('anonymous');
        return;
      }

      // Step 2: user is authed — resolve their active context to pick the
      // right dashboard root (same logic as DashboardGuard).
      let dest = '/dashboard'; // safe fallback
      try {
        const wsRes = await fetch('/api/me/workspaces', { credentials: 'same-origin' });
        if (wsRes.ok) {
          const wsData = (await wsRes.json()) as WorkspacesData;
          if (wsData.ok && wsData.data) {
            dest = resolveContextHome(wsData.data);
          }
        }
      } catch {
        // Ignore — fall back to /dashboard which DashboardGuard resolves.
      }

      if (cancelled) return;

      dashboardHref.current = dest;
      // No redirect — marketing page always renders. Just update the CTA.
      setAuthState('authed');
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ─── NAV CTA SWAP (DOM mutation, consistent with this file's inline pattern) ─
  useEffect(() => {
    if (authState === 'probing') return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const ctaGroup = wrapper.querySelector<HTMLElement>('.nav-cta-group');
    if (!ctaGroup) return;

    if (authState === 'anonymous') {
      // Default Sign in / Get started CTAs — they're already in the markup.
      // Nothing to swap; just ensure they're visible (in case of probe-hide).
      ctaGroup.style.opacity = '';
      ctaGroup.style.pointerEvents = '';
      return;
    }

    // authState === 'authed' — swap CTA to "Go to dashboard →".
    const href = dashboardHref.current;
    ctaGroup.innerHTML = `
      <a href="${href}" data-spa-link class="btn btn-primary">Go to dashboard →</a>
    `;
    ctaGroup.style.opacity = '';
    ctaGroup.style.pointerEvents = '';
  }, [authState]);

  // ─── MARKETING INTERACTIONS ───────────────────────────────────────────────
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // ─── PRICING TAB SWITCH ──────────────────────
    const pgIndividual = wrapper.querySelector<HTMLElement>('#pricing-individual');
    const pgBusiness = wrapper.querySelector<HTMLElement>('#pricing-business');
    const pricingTabs = Array.from(
      wrapper.querySelectorAll<HTMLButtonElement>('[data-pricing-tab]'),
    );
    const pricingTabHandlers = new Map<HTMLButtonElement, () => void>();
    pricingTabs.forEach((btn) => {
      const handler = (): void => {
        pricingTabs.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        if (!pgIndividual || !pgBusiness) return;
        if (btn.dataset.pricingTab === 'individual') {
          pgIndividual.style.display = 'grid';
          pgBusiness.style.display = 'none';
        } else {
          pgIndividual.style.display = 'none';
          pgBusiness.style.display = 'grid';
          pgBusiness.querySelectorAll('.price-card').forEach((c) => c.classList.add('visible'));
        }
      };
      pricingTabHandlers.set(btn, handler);
      btn.addEventListener('click', handler);
    });

    // ─── STICKY NAV COMPRESS ─────────────────────
    const nav = wrapper.querySelector<HTMLElement>('nav.site-nav');
    const onScroll = (): void => {
      if (!nav) return;
      nav.classList.toggle('scrolled', window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // ─── SCROLL REVEALS (universal) ──────────────
    const revealObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in-view');
            revealObs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );
    wrapper.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => revealObs.observe(el));

    // ─── HERO SKILL BARS (animate when in view) ──
    const heroMockup = wrapper.querySelector<HTMLElement>('.hero-mockup');
    let barObs: IntersectionObserver | null = null;
    if (heroMockup) {
      barObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              setTimeout(() => heroMockup.classList.add('bars-animate'), 600);
              barObs?.unobserve(e.target);
            }
          });
        },
        { threshold: 0.3 },
      );
      barObs.observe(heroMockup);
    }

    // ─── ANIMATED COUNTERS ───────────────────────
    const counters = Array.from(wrapper.querySelectorAll<HTMLElement>('.counter'));
    const counterObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const target = e.target as HTMLElement;
          if (e.isIntersecting && !target.dataset.done) {
            target.dataset.done = 'true';
            const targetValue = parseFloat(target.dataset.target ?? '0');
            const decimal = target.dataset.decimal === '1';
            const suffix = target.dataset.suffix ?? '';
            const duration = 1600;
            const start = performance.now();
            const animate = (now: number): void => {
              const progress = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              const value = targetValue * eased;
              if (decimal) {
                target.innerHTML = (value / 10).toFixed(1) + suffix;
              } else {
                target.innerHTML = Math.floor(value).toLocaleString() + suffix;
              }
              if (progress < 1) {
                requestAnimationFrame(animate);
              } else if (decimal) {
                target.innerHTML = (targetValue / 10).toFixed(1) + suffix;
              } else {
                target.innerHTML = targetValue.toLocaleString() + suffix;
              }
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.4 },
    );
    counters.forEach((c) => counterObs.observe(c));

    // ─── MAGNETIC BUTTONS ────────────────────────
    const magneticBtns = Array.from(wrapper.querySelectorAll<HTMLElement>('.btn-magnetic'));
    const magneticHandlers = new Map<
      HTMLElement,
      { move: (e: MouseEvent) => void; leave: () => void }
    >();
    magneticBtns.forEach((btn) => {
      const move = (e: MouseEvent): void => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = `translate(${x * 0.18}px, ${y * 0.25}px)`;
      };
      const leave = (): void => {
        btn.style.transform = 'translate(0, 0)';
      };
      magneticHandlers.set(btn, { move, leave });
      btn.addEventListener('mousemove', move);
      btn.addEventListener('mouseleave', leave);
    });

    // ─── FINAL CTA SPOTLIGHT ─────────────────────
    const ctaSection = wrapper.querySelector<HTMLElement>('.final-cta');
    const ctaMoveHandler = (e: MouseEvent): void => {
      if (!ctaSection) return;
      const rect = ctaSection.getBoundingClientRect();
      ctaSection.style.setProperty('--mx', `${e.clientX - rect.left}px`);
      ctaSection.style.setProperty('--my', `${e.clientY - rect.top}px`);
    };
    if (ctaSection) {
      ctaSection.addEventListener('mousemove', ctaMoveHandler);
    }

    // ─── TAB TOGGLES (catalogue + pricing) ───────
    const tabGroups = Array.from(wrapper.querySelectorAll<HTMLElement>('.tab-toggle'));
    const tabGroupHandlers: Array<{ btn: HTMLButtonElement; handler: () => void }> = [];
    tabGroups.forEach((group) => {
      const groupBtns = Array.from(group.querySelectorAll<HTMLButtonElement>('.tab-btn'));
      groupBtns.forEach((btn) => {
        const handler = (): void => {
          groupBtns.forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
        };
        btn.addEventListener('click', handler);
        tabGroupHandlers.push({ btn, handler });
      });
    });

    // ─── REDUCED MOTION ──────────────────────────
    if (typeof window.matchMedia === 'function') {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reduced.matches) {
        wrapper.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => {
          el.classList.add('in-view');
        });
        magneticBtns.forEach((btn) => {
          btn.style.transform = '';
        });
      }
    }

    // ─── CLEANUP ─────────────────────────────────
    return () => {
      window.removeEventListener('scroll', onScroll);
      revealObs.disconnect();
      barObs?.disconnect();
      counterObs.disconnect();
      if (ctaSection) ctaSection.removeEventListener('mousemove', ctaMoveHandler);
      pricingTabHandlers.forEach((handler, btn) => btn.removeEventListener('click', handler));
      magneticHandlers.forEach(({ move, leave }, btn) => {
        btn.removeEventListener('mousemove', move);
        btn.removeEventListener('mouseleave', leave);
      });
      tabGroupHandlers.forEach(({ btn, handler }) => btn.removeEventListener('click', handler));
    };
  }, []);

  // ─── INITIAL NAV CTA HIDE (applied synchronously on first render via ref) ──
  // We hide the CTA group briefly during the probe to avoid flashing
  // "Sign in / Get started" to authed users (who will see "Go to dashboard →"
  // once the probe resolves). The page itself is always fully visible.
  // The style is applied via an inline ref callback to avoid a separate effect
  // that would race with the markup paint.
  const handleWrapperRef = (el: HTMLDivElement | null): void => {
    (wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    if (!el) return;
    const ctaGroup = el.querySelector<HTMLElement>('.nav-cta-group');
    if (ctaGroup) {
      ctaGroup.style.opacity = '0';
      ctaGroup.style.pointerEvents = 'none';
    }
  };

  return (
    <div
      ref={handleWrapperRef}
      className="bedd-marketing bedd-home"
      dangerouslySetInnerHTML={{ __html: landingMarkup }}
    />
  );
}
